<template>
    <div :key="iteration" :id="'chart-' + this.id">
        <loading v-show="isLoading"></loading>

        <header v-show="title.show">
            <h3 :style="{ textAlign: title.alignment, color: title.colour }">
                {{ title.text }}
            </h3>

            <widget-description v-if="title.description" :content="title.description"></widget-description>
        </header>

        <template v-for="textArea in textAreas">
            <template v-if="textArea.position === 'top'">
                <text-extended
                    :title="textArea.title"
                    :sections="textArea.sections"
                    :fontSize="retrieveFontSize(textArea)"
                />
            </template>
        </template>

        <div class="chart-wrapper">
            <canvas ref="canvas" :id="'canvas-' + this.id" :class="classes" role="img">
                <p class="error">This chart can't be shown as your browser does not support HTML canvas.</p>
            </canvas>

            <chart-legend v-if="legend" v-show="retrieveDisplay(legend) !== 'none'" :legend="legend"></chart-legend>
        </div>

        <template v-for="textArea in textAreas">
            <template v-if="textArea.position === 'bottom'">
                <text-extended
                    :title="textArea.title"
                    :sections="textArea.sections"
                    :fontSize="retrieveFontSize(textArea)"
                />
            </template>
        </template>
    </div>
</template>

<script>
    import ApiWidget from './ApiWidget';
    import MargettsHelpers from '../modules/helpers';
    import Charts from '../modules/margetts-charts'
    import ChartLegend from "./ChartLegend.vue";
    import TextExtended from "./TextExtended.vue";
    import WidgetDescription from "./WidgetDescription.vue";
    import Loading from "./Loading.vue";

    export default {
        components: {Loading, WidgetDescription, TextExtended, ChartLegend},
        extends: ApiWidget,
        props: {
            donut: Boolean,
            id: Number
        },

        data() {
            return {
                title: {},
                legend: false,
            }
        },

        created() {
            this.registerParameterListeners([
                'modelRange',
                'portfolio',
                'range',
                'riskRated',
                'scheme',
                'searchString',
                'startDate',
                'endDate',
                'tab'
            ]);
        },

        methods: {
            async update(data) {
                if (data.display === 'none') {
                    return this.$el.closest('.page-widget').hidden = true;
                }

                this.title = this.retrieveTitle(data);
                this.legend = data.legend;
                this.textAreas = this.retrieveTextAreas(data);

                switch (data.type) {
                    case 'horizontal-bar':
                        this.renderHorizontalBar(data);
                        break;
                    case 'line':
                        this.renderLine(data);
                        break;
                    case 'pie':
                        this.renderPie(data);
                        break;
                    case 'donut':
                        this.renderDonut(data);
                        break;
                    default:
                        this.throwApiError(data.errors ? data.errors.message : 'Unknown chart type');
                        break;
                }
            },

            renderHorizontalBar(data) {
                if (! data.bar_categories) {
                    return;
                }

                const values = [];
                const labels = [];
                const colours = [];

                const canvas = this.$refs.canvas;
                const ctx = canvas.getContext('2d');

                ctx.clearRect(0, 0, canvas.width, canvas.height);

                let options;
                let tooltips = false;

                [].forEach.call(data.bar_categories, category => {
                    values.push(category.bars[0].value);
                    labels.push(category.value);
                    colours.push(this.retrieveColour(category));
                    tooltips = category.format.tooltips.enabled;
                });

                options = {
                    animation: false,
                    defaultFontSize: 12,
                    indexAxis: 'y',
                    maintainAspectRatio: false,
                    plugins: {
                        tooltip: {
                            enabled: tooltips
                        },
                        legend: {
                            display: data.legend ? data.legend : false
                        },
                    },
                    scales: {
                        y: {
                            grid: {
                                display: false
                            }
                        }
                    }
                };

                return Charts.WIDGETS.horizontalBar(ctx, values, labels, colours, options);
            },

            renderLine(data) {
                if (! data.series) {
                    return;
                }

                const labels = [];
                const values = [];
                const colours = [];
                const names = [];

                let radius = 0;
                let tooltips = false;

                const canvas = this.$refs.canvas;
                const ctx = canvas.getContext('2d');

                ctx.clearRect(0, 0, canvas.width, canvas.height);

                let options;

                [].forEach.call(data.series, series => {
                    const seriesLabels = [];
                    const seriesValues = [];
                    const seriesNames = [];

                    [].forEach.call(series.points, point => {
                        seriesLabels.push(point.x);
                        seriesValues.push(point.y);
                    });

                    seriesNames.push(series.name);

                    tooltips = series.format.tooltips.enabled;
                    radius = series.format.point.radius;

                    labels.push(seriesLabels);
                    values.push(seriesValues);
                    colours.push(this.retrieveColour(series));
                    names.push(seriesNames);
                });

                options = {
                    animation: false,
                    elements: {
                        point: {
                            radius: radius
                        },
                        line: {
                            borderColor: data.series.colour,
                            borderWidth: 1,
                            tension: 0
                        }
                    },
                    maintainAspectRatio: false,
                    plugins: {
                        tooltip: {
                            enabled: tooltips
                        },
                        legend: {
                            display: data.legend,
                            position: data.legend.position,
                            onClick: e => e.stopPropagation()
                        },
                    },
                    responsive: true,
                    scales: {
                        x: {
                            border: {
                                display: false,
                            },
                            grid: {
                                display: false,
                            },
                            scaleLabel: {
                                display: data.axes.x.title.display !== 'none',
                                labelString: data.axes.x.title.value,
                            },
                            type: data.axes.x.format.type,
                            time: {
                                unit: data.axes.x.format.time.unit,
                            },
                            ticks: {
                                callback: value => {
                                    return MargettsHelpers.formatValue(value, data.axes.x.format);
                                }
                            }
                        },
                        y: {
                            border: {
                                display: false,
                            },
                            grid: {
                                color: function(context) {
                                    return context.tick.value === 0 ? '#cccccc' : 'transparent';
                                },
                                display: true,
                                lineWidth: 1,
                            },
                            scaleLabel: {
                                display: data.axes.y.title.display !== 'none',
                                labelString: data.axes.y.title.value
                            },
                            ticks: {
                                callback: value => {
                                    return MargettsHelpers.formatValue(value, data.axes.y.format);
                                }
                            }
                        }
                    }
                };

                return Charts.WIDGETS.line(ctx, values, labels, colours, options, names);
            },

            renderPie(data) {
                if (! data.segments) {
                    return;
                }

                const values = [];
                const labels = [];
                const colours = [];

                const canvas = this.$refs.canvas;
                const ctx = canvas.getContext('2d');

                ctx.clearRect(0, 0, canvas.width, canvas.height);

                let options;
                let tooltips = false;

                [].forEach.call(data.segments, segment => {
                    tooltips = segment.format.tooltips.enabled;
                    values.push(segment.value);
                    labels.push(segment.name);
                    colours.push(this.retrieveColour(segment));
                });

                options = {
                    animation: false,
                    aspectRatio: 2,
                    events: ['click', 'mousemove'],
                    hover: {
                        onHover: function (event, elements) {
                            if (event.type === 'mousemove') {
                                document.body.style.cursor = typeof elements[0] !== 'undefined' ? 'pointer' : 'default';
                            }

                            if (event.type === 'click') {
                                if (typeof elements[0] === 'undefined') {
                                    Charts.TOOLTIPS.hide();
                                } else {
                                    Charts.TOOLTIPS.show(data.action, data.segments, elements[0], event);
                                }
                            }
                        }
                    },
                    defaultFontSize: 12,
                    plugins: {
                        tooltip: {
                            enabled: tooltips,
                            callbacks: {
                                label: function(tooltipItem) {
                                    return tooltipItem.label || '';
                                }
                            },
                        },
                        legend: {
                            display: false,
                        },
                    },
                    responsive: true
                };

                return Charts.WIDGETS.pie(ctx, values, labels, colours, options, this.donut);
            },

            renderDonut(data) {
                this.donut = true;
                return this.renderPie(data);
            }
        }
    }
</script>
